import * as Sentry from '@sentry/react'
import { browserTracingIntegration } from '@sentry/react'
import { ReactNode, useEffect } from 'react'

export interface SentryProviderProps {
  children: ReactNode
  dsn?: string
  environment?: string
  release?: string
  tracesSampleRate?: number
}

/**
 * `SentryProvider` is a React functional component that initializes the Sentry SDK.
 *
 * Props:
 * @param children (ReactNode) - The child components to be wrapped by the Sentry provider.
 * @param dsn (string) - The Sentry Data Source Name. If not provided, Sentry will not be initialized.
 * @param environment (string)- The environment in which the application is running (e.g., 'production', 'development').
 * @param release (string) - The release version of the application.
 * @param tracesSampleRate (number) - The sampling rate for tracing, between 0 and 1. Defaults to 0.2.
 *
 * Returns:
 * The children components wrapped by the provider.
 *
 * This function:
 * It takes configuration options as props and initializes Sentry when the component mounts,
 * provided a DSN is supplied.
 *
 * If no DSN is provided, a warning is logged to the console, and Sentry is not initialized.
 */
export const SentryProvider: React.FC<SentryProviderProps> = ({
  children,
  dsn,
  environment,
  release,
  tracesSampleRate = 0.2,
}): React.ReactElement => {
  useEffect(() => {
    if (dsn && !Sentry.isInitialized()) {
      Sentry.init({
        dsn,
        integrations: [browserTracingIntegration()],
        tracesSampleRate,
        environment,
        release,
      })
    } else {
      console.warn('Sentry DSN not provided. Sentry will not be initialized.')
    }
  }, [dsn, environment, release, tracesSampleRate])

  return <>{children}</>
}
